import React, { useEffect, useState, type ReactNode, type Ref } from "react";

import { styled, type CSSProperties } from "styled-components";

import { gridSpacing, colorTheme } from "@utils";

import Button from "../Button/Button";
import Collapse from "../Collapse/Collapse";

const PanelStyle = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  border: 1px solid ${colorTheme("neutralL4")};
  background: ${colorTheme("white")};
  box-shadow: 3px 6px 6px 0px rgba(0, 0, 0, 0.025);
`;

export const PanelHeader = styled.div<{
  $opened?: boolean;
  $collapsible?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid
    ${({ $opened, $collapsible }) =>
      $opened || !$collapsible ? colorTheme("neutralL4") : "transparent"};
  padding-bottom: ${({ $opened }) => ($opened ? gridSpacing[2] : 0)}px;
  margin-bottom: ${({ $opened }) => ($opened ? gridSpacing[3] : 0)}px;
  font-size: 14px;
  font-weight: 500;
  padding: 0 ${gridSpacing[4]}px;
  height: 70px;

  ${({ $collapsible }) =>
    $collapsible &&
    `
      &:hover {
        background: ${colorTheme("neutralL5")};
        cursor: pointer;
      }
  `}
`;

const Panel = ({
  children,
  header,
  collapsible,
  defaultOpened = false,
  headerItems,
  hideHeaderItemsOnCollapse = false,
  style,
  headerStyle,
  onToggle,
  ref,
}: {
  children?: ReactNode;
  header?: string | ReactNode;
  collapsible?: boolean;
  defaultOpened?: boolean;
  headerItems?: ReactNode;
  hideHeaderItemsOnCollapse?: boolean;
  style?: CSSProperties;
  headerStyle?: CSSProperties;
  onToggle?: (opened: boolean) => void;
  ref?: Ref<HTMLDivElement>;
}) => {
  const [opened, setOpened] = useState(defaultOpened);
  let body = children;

  if (collapsible) {
    if (!header) {
      console.error("Collapsible panel must have a header");
    }

    body = <Collapse isOpened={opened}>{children}</Collapse>;
  }

  useEffect(() => {
    setOpened(defaultOpened);
  }, [defaultOpened]);

  return (
    <PanelStyle ref={ref} style={style}>
      {header && (
        <PanelHeader
          $collapsible={collapsible}
          $opened={opened}
          onClick={() => {
            if (collapsible) {
              const next = !opened;
              setOpened(next);
              onToggle?.(next);
            }
          }}
          style={headerStyle}
        >
          {header}

          <div style={{ display: "flex", gap: gridSpacing[2] }}>
            {(!hideHeaderItemsOnCollapse || opened) && headerItems}
            {collapsible && (
              <Button
                small
                tooltip={opened ? "Collapse" : "Expand"}
                tooltipDirection="left"
                variant="neutralLink"
                testId="collapse-button"
                icon={`fa-chevron-${opened ? "down" : "right"}`}
              />
            )}
          </div>
        </PanelHeader>
      )}
      <div
        style={{
          paddingLeft: gridSpacing[4],
          paddingRight: gridSpacing[4],
          paddingBottom: opened ? gridSpacing[4] : 0,
        }}
      >
        {body}
      </div>
    </PanelStyle>
  );
};

export default Panel;
